import React, { useState } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, ProgressBar, Table, Badge } from 'react-bootstrap';
import { FaUpload, FaCheckCircle, FaTimesCircle, FaFilePdf } from 'react-icons/fa';
import axios from 'axios';

const API_URL = process.env.NODE_ENV === 'development' 
  ? 'http://localhost:4004' 
  : '';

const CargaRecibos = () => {
  const [mes, setMes] = useState('');
  const [secuencia, setSecuencia] = useState('');
  const [archivos, setArchivos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [resultados, setResultados] = useState(null);
  const [error, setError] = useState(null);
  const [mostrarTodosArchivos, setMostrarTodosArchivos] = useState(false);
  const [mostrarTodosDetalles, setMostrarTodosDetalles] = useState(false);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    
    // Validar que sean PDFs
    const pdfFiles = files.filter(f => f.type === 'application/pdf' || f.name.endsWith('.pdf'));
    
    if (pdfFiles.length !== files.length) {
      setError('Solo se permiten archivos PDF');
      return;
    }

    // Validar nombres (formato: YYYYMMSOXXX donde XXX = legajo)
    const archivosConLegajo = pdfFiles.map(file => {
      const nombreSinExt = file.name.replace(/\.pdf$/i, '');
      const regex = /^(\d{4})(\d{2})(\d{1})O(\d+)$/i;
      const match = nombreSinExt.match(regex);
      
      if (match) {
        const [, anio, mesArchivo, secArchivo, legajo] = match;
        return {
          file,
          legajo,
          anio,
          mes: mesArchivo,
          secuencia: secArchivo,
          valido: true,
          error: null
        };
      } else {
        return {
          file,
          legajo: null,
          valido: false,
          error: 'Nombre de archivo inválido. Formato esperado: YYYYMMSOXXX.pdf'
        };
      }
    });

    setArchivos(archivosConLegajo);
    setError(null);
  };

  const subirArchivos = async () => {
    if (!mes || !secuencia) {
      setError('Por favor completa mes y secuencia');
      return;
    }

    const archivosValidos = archivos.filter(a => a.valido);
    if (archivosValidos.length === 0) {
      setError('No hay archivos válidos para subir');
      return;
    }

    setUploading(true);
    setProgress(0);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      
      // Dividir archivos en lotes de 50
      const LOTE_SIZE = 50;
      const totalArchivos = archivosValidos.length;
      const totalLotes = Math.ceil(totalArchivos / LOTE_SIZE);
      
      let todosLosResultados = {
        exitosos: 0,
        errores: 0,
        detalles: []
      };

      // Subir cada lote secuencialmente
      for (let i = 0; i < totalLotes; i++) {
        const inicio = i * LOTE_SIZE;
        const fin = Math.min(inicio + LOTE_SIZE, totalArchivos);
        const lote = archivosValidos.slice(inicio, fin);
        
        console.log(`📦 Subiendo lote ${i + 1}/${totalLotes} (${lote.length} archivos)`);
        
        const formData = new FormData();
        formData.append('mes', mes);
        formData.append('secuencia', secuencia);
        
        lote.forEach((archivo, index) => {
          formData.append('files', archivo.file);
          formData.append(`legajo_${index}`, archivo.legajo);
        });

        const response = await axios.post(`${API_URL}/api/recibos/upload`, formData, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            // Calcular progreso total considerando todos los lotes
            const loteProgress = (progressEvent.loaded * 100) / progressEvent.total;
            const progressoLotesAnteriores = (i / totalLotes) * 100;
            const progressoLoteActual = (loteProgress / totalLotes);
            const percentCompleted = Math.round(progressoLotesAnteriores + progressoLoteActual);
            setProgress(percentCompleted);
          }
        });

        // Acumular resultados
        if (response.data?.data) {
          todosLosResultados.exitosos += response.data.data.exitosos || 0;
          todosLosResultados.errores += response.data.data.errores || 0;
          todosLosResultados.detalles.push(...(response.data.data.detalles || []));
        }
      }

      setProgress(100);
      setResultados({
        success: todosLosResultados.errores === 0,
        message: `Carga completada: ${todosLosResultados.exitosos} exitosos, ${todosLosResultados.errores} errores`,
        data: todosLosResultados
      });

    } catch (error) {
      console.error('Error al subir archivos:', error);
      setError(error.response?.data?.message || 'Error al subir los archivos');
    } finally {
      setUploading(false);
    }
  };

  const resetear = () => {
    setArchivos([]);
    setMes('');
    setSecuencia('');
    setProgress(0);
    setResultados(null);
    setError(null);
    setMostrarTodosArchivos(false);
    setMostrarTodosDetalles(false);
    document.getElementById('file-input').value = '';
  };

  return (
    <Container fluid className="py-4">
      <Row>
        <Col>
          <Card>
            <Card.Header>
              <h4 className="mb-0">
                <FaUpload className="me-2" />
                Carga Masiva de Recibos
              </h4>
            </Card.Header>
            <Card.Body>
              {/* Configuración inicial */}
              <Row className="mb-4">
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Mes</Form.Label>
                    <Form.Select
                      value={mes}
                      onChange={(e) => setMes(e.target.value)}
                      disabled={uploading}
                    >
                      <option value="">Seleccionar mes...</option>
                      <option value="01">Enero</option>
                      <option value="02">Febrero</option>
                      <option value="03">Marzo</option>
                      <option value="04">Abril</option>
                      <option value="05">Mayo</option>
                      <option value="06">Junio</option>
                      <option value="07">Julio</option>
                      <option value="08">Agosto</option>
                      <option value="09">Septiembre</option>
                      <option value="10">Octubre</option>
                      <option value="11">Noviembre</option>
                      <option value="12">Diciembre</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Secuencia</Form.Label>
                    <Form.Select
                      value={secuencia}
                      onChange={(e) => setSecuencia(e.target.value)}
                      disabled={uploading}
                    >
                      <option value="">Seleccionar secuencia...</option>
                      <option value="1">1 - Recibos</option>
                      <option value="2">2 - Extras (aguinaldo, cuotas, etc.)</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              {/* Selector de archivos */}
              <Form.Group className="mb-4">
                <Form.Label>Archivos PDF</Form.Label>
                <Form.Control
                  id="file-input"
                  type="file"
                  multiple
                  accept=".pdf"
                  onChange={handleFileChange}
                  disabled={uploading}
                />
                <Form.Text className="text-muted">
                  Formato esperado: YYYYMMSOXXX.pdf (ej: 2024121O9054.pdf)
                </Form.Text>
              </Form.Group>

              {/* Mensajes */}
              {error && (
                <Alert variant="danger">
                  {error}
                </Alert>
              )}

              {/* Lista de archivos */}
              {archivos.length > 0 && (
                <Card className="mb-4">
                  <Card.Header className="d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">
                      Archivos seleccionados ({archivos.length})
                      {archivos.filter(a => a.valido).length !== archivos.length && (
                        <Badge bg="warning" className="ms-2">
                          {archivos.filter(a => !a.valido).length} inválidos
                        </Badge>
                      )}
                    </h6>
                    {archivos.length > 10 && (
                      <Button 
                        size="sm" 
                        variant="link"
                        onClick={() => setMostrarTodosArchivos(!mostrarTodosArchivos)}
                      >
                        {mostrarTodosArchivos ? 'Mostrar menos' : `Mostrar todos (${archivos.length})`}
                      </Button>
                    )}
                  </Card.Header>
                  <Card.Body className="p-0">
                    <Table striped className="mb-0">
                      <thead>
                        <tr>
                          <th>Archivo</th>
                          <th>Legajo</th>
                          <th>Estado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(mostrarTodosArchivos ? archivos : archivos.slice(0, 10)).map((archivo, index) => (
                          <tr key={index}>
                            <td>
                              <FaFilePdf className="text-danger me-2" />
                              {archivo.file.name}
                            </td>
                            <td>{archivo.legajo || '-'}</td>
                            <td>
                              {archivo.valido ? (
                                <Badge bg="success">
                                  <FaCheckCircle className="me-1" />
                                  Válido
                                </Badge>
                              ) : (
                                <Badge bg="danger">
                                  <FaTimesCircle className="me-1" />
                                  {archivo.error}
                                </Badge>
                              )}
                            </td>
                          </tr>
                        ))}
                        {!mostrarTodosArchivos && archivos.length > 10 && (
                          <tr>
                            <td colSpan="3" className="text-center text-muted">
                              <small>... y {archivos.length - 10} archivos más</small>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              )}

              {/* Progreso de carga */}
              {uploading && (
                <div className="mb-4">
                  <div className="d-flex justify-content-between mb-2">
                    <span>Subiendo archivos en lotes de 50...</span>
                    <span>{progress}%</span>
                  </div>
                  <ProgressBar now={progress} animated />
                  <small className="text-muted mt-1 d-block">
                    Los archivos se suben en grupos para evitar problemas con el servidor
                  </small>
                </div>
              )}

              {/* Resultados */}
              {resultados && (
                <Alert variant={resultados.success ? 'success' : 'danger'}>
                  <h6>Resultado de la carga:</h6>
                  <p className="mb-2">
                    <strong>{resultados.message || `${resultados.data?.exitosos || 0} archivos subidos correctamente, ${resultados.data?.errores || 0} errores`}</strong>
                  </p>
                  
                  {resultados.data?.detalles && resultados.data.detalles.length > 0 && (
                    <div className="mt-2">
                      {resultados.data.errores > 0 ? (
                        // Si hay errores, mostrar solo los errores
                        <>
                          <small><strong>Errores encontrados:</strong></small>
                          <ul className="mb-0 mt-1" style={{ fontSize: '0.9em' }}>
                            {resultados.data.detalles
                              .filter(detalle => !detalle.success)
                              .map((detalle, index) => (
                                <li key={index} className="text-danger">
                                  {detalle.archivo}: ✗ {detalle.error}
                                </li>
                              ))}
                          </ul>
                        </>
                      ) : (
                        // Si no hay errores, mostrar resumen colapsable
                        <>
                          <Button 
                            size="sm" 
                            variant="link" 
                            className="p-0"
                            onClick={() => setMostrarTodosDetalles(!mostrarTodosDetalles)}
                          >
                            {mostrarTodosDetalles ? '▼ Ocultar detalles' : `▶ Ver detalles (${resultados.data.detalles.length} archivos)`}
                          </Button>
                          
                          {mostrarTodosDetalles && (
                            <ul className="mb-0 mt-1" style={{ fontSize: '0.9em', maxHeight: '300px', overflowY: 'auto' }}>
                              {resultados.data.detalles.map((detalle, index) => (
                                <li key={index}>
                                  {detalle.archivo}: {detalle.success ? `✓ Legajo ${detalle.legajo}` : `✗ ${detalle.error}`}
                                </li>
                              ))}
                            </ul>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </Alert>
              )}

              {/* Botones */}
              <div className="d-flex gap-2">
                <Button
                  variant="primary"
                  onClick={subirArchivos}
                  disabled={uploading || archivos.length === 0 || !mes || !secuencia}
                >
                  {uploading ? 'Subiendo...' : 'Subir Recibos'}
                </Button>
                <Button
                  variant="secondary"
                  onClick={resetear}
                  disabled={uploading}
                >
                  Resetear
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default CargaRecibos;